import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import './GlobalAuditLogs.scss';
import Loader from '../../components/BaseComponents/Loader';
import { RootState } from '../../store/store';
import { UserRoleEnum } from '../../types/apiGeneratedTypes';
import { AuditLog, getAllMemberAuditLogs } from '../../api/members';
import {
  MEMBER_PROFILES_MERGED_ACTION,
  buildMergeProfileDetailRows,
} from '../../utils/auditLogMergeDetails';

const isSuperAdminUser = (admin: RootState['admin']['admin']) =>
  admin.role === UserRoleEnum.SuperAdmin && admin.is_super_admin === true;

const formatDateTime = (dateString: string): { date: string; time: string } => {
  const date = new Date(dateString);
  return {
    date: `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${date.getFullYear()}`,
    time: `${date.getHours().toString().padStart(2, '0')}:${date
      .getMinutes()
      .toString()
      .padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`,
  };
};

const formatActionType = (actionType: string): string =>
  actionType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const AI_ACTION_TYPES = new Set([
  'ask_your_results_chat',
  'gp_snapshot_generation',
  'personalised_monitoring_plan_generation',
]);

const formatRoleLabel = (role: string): string => {
  if (!role) return '-';
  if (role.toLowerCase() === 'client') return 'Member';
  return role
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const renderJsonValue = (value: unknown): React.ReactNode => {
  if (value === null || value === undefined) {
    return <span className='json-primitive'>null</span>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className='json-primitive'>[]</span>;
    return (
      <ul className='json-list'>
        {value.map((item, index) => (
          <li key={index} className='json-item'>
            <span className='json-key'>[{index}]</span>
            <span className='json-separator'>: </span>
            {renderJsonValue(item)}
          </li>
        ))}
      </ul>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return <span className='json-primitive'>{'{}'}</span>;
    return (
      <ul className='json-list'>
        {entries.map(([key, nestedValue]) => (
          <li key={key} className='json-item'>
            <span className='json-key'>{key}</span>
            <span className='json-separator'>: </span>
            {renderJsonValue(nestedValue)}
          </li>
        ))}
      </ul>
    );
  }

  return <span className='json-primitive'>{String(value)}</span>;
};

const extractAskYourResultsQuestion = (actionDetails: unknown): string => {
  let details: any = actionDetails;
  if (typeof actionDetails === 'string') {
    try {
      details = JSON.parse(actionDetails);
    } catch {
      // keep as string
      details = actionDetails;
    }
  }

  const question =
    (typeof details?.question === 'string' && details.question) ||
    (typeof details?.user_question === 'string' && details.user_question) ||
    (typeof details?.prompt === 'string' && details.prompt) ||
    (typeof details?.text === 'string' && details.text) ||
    (typeof details === 'string' && details) ||
    '';

  return question.trim();
};

const coerceToObjectDetails = (actionDetails: unknown): Record<string, unknown> | null => {
  if (actionDetails === null || actionDetails === undefined) return null;

  if (typeof actionDetails === 'string') {
    try {
      const parsed = JSON.parse(actionDetails);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
      return null;
    } catch {
      return null;
    }
  }

  if (typeof actionDetails === 'object' && !Array.isArray(actionDetails)) {
    return actionDetails as Record<string, unknown>;
  }

  return null;
};

const extractAskYourResultsAnswer = (actionDetails: unknown): string => {
  const details = coerceToObjectDetails(actionDetails);
  const candidate =
    (typeof details?.answer === 'string' && details.answer) ||
    (typeof details?.response === 'string' && details.response) ||
    (typeof details?.assistant_response === 'string' && details.assistant_response) ||
    (typeof details?.assistantAnswer === 'string' && details.assistantAnswer) ||
    (typeof details?.completion === 'string' && details.completion) ||
    (typeof details?.output === 'string' && details.output) ||
    (typeof details?.result === 'string' && details.result) ||
    (typeof details?.message === 'string' && details.message) ||
    '';

  return candidate.trim();
};

const extractGpSnapshotQuestion = (actionDetails: unknown): string => {
  const details = coerceToObjectDetails(actionDetails);
  const candidate =
    (typeof details?.prompt === 'string' && details.prompt) ||
    (typeof details?.question === 'string' && details.question) ||
    (typeof details?.user_question === 'string' && details.user_question) ||
    (typeof details?.text === 'string' && details.text) ||
    (typeof details?.input === 'string' && details.input) ||
    (typeof details?.request === 'string' && details.request) ||
    (typeof details?.query === 'string' && details.query) ||
    '';

  return candidate.trim();
};

const extractGpSnapshotAnswer = (actionDetails: unknown): string => {
  const details = coerceToObjectDetails(actionDetails);
  const candidate =
    (typeof details?.snapshot === 'string' && details.snapshot) ||
    (typeof details?.summary === 'string' && details.summary) ||
    (typeof details?.content === 'string' && details.content) ||
    (typeof details?.answer === 'string' && details.answer) ||
    (typeof details?.response === 'string' && details.response) ||
    (typeof details?.assistant_response === 'string' && details.assistant_response) ||
    (typeof details?.completion === 'string' && details.completion) ||
    (typeof details?.output === 'string' && details.output) ||
    (typeof details?.result === 'string' && details.result) ||
    '';

  return candidate.trim();
};

const getQuestionAnswerForLog = (
  actionType: string,
  actionDetails: unknown
): { question: string; answer: string; omitKeys: string[] } | null => {
  if (actionType === 'ask_your_results_chat') {
    return {
      question: extractAskYourResultsQuestion(actionDetails),
      answer: extractAskYourResultsAnswer(actionDetails),
      omitKeys: [
        'question',
        'user_question',
        'prompt',
        'text',
        'answer',
        'response',
        'assistant_response',
        'assistantAnswer',
        'completion',
        'output',
        'result',
        'message',
      ],
    };
  }

  if (actionType === 'gp_snapshot_generation' || actionType === 'personalised_monitoring_plan_generation') {
    return {
      question: extractGpSnapshotQuestion(actionDetails),
      answer: extractGpSnapshotAnswer(actionDetails),
      omitKeys: [
        'prompt',
        'question',
        'user_question',
        'text',
        'input',
        'request',
        'query',
        'snapshot',
        'summary',
        'content',
        'answer',
        'response',
        'assistant_response',
        'completion',
        'output',
        'result',
        'message',
      ],
    };
  }

  return null;
};

const buildOtherInfoDetails = (
  actionDetails: unknown,
  keysToOmit: string[]
): Record<string, unknown> | unknown => {
  const detailsObj = coerceToObjectDetails(actionDetails);
  if (!detailsObj) return actionDetails;

  const omit = new Set(keysToOmit);
  const other: Record<string, unknown> = {};
  Object.entries(detailsObj).forEach(([key, value]) => {
    if (!omit.has(key)) other[key] = value;
  });

  return other;
};

const getActionPreview = (log: AuditLog): React.ReactNode => {
  if (log.action_type === 'ask_your_results_chat') {
    const question = extractAskYourResultsQuestion(log?.action_details);
    return (
      <div className='audit-action'>
        <div className='audit-action-title'>{formatActionType(log.action_type)}</div>
        <div className='audit-action-question'>{question || '—'}</div>
      </div>
    );
  }
  return formatActionType(log.action_type);
};

const GlobalAuditLogs = () => {
  const navigate = useNavigate();
  const adminInfo = useSelector((state: RootState) => state.admin);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [limit, setLimit] = useState<number>(200);
  const [offset, setOffset] = useState<number>(0);

  useEffect(() => {
    if (!isSuperAdminUser(adminInfo.admin)) {
      toast.error('Not authorized');
      navigate('/', { replace: true });
    }
  }, [adminInfo.admin, navigate]);

  const fetchLogs = async (nextLimit: number = limit, nextOffset: number = offset) => {
    setIsLoading(true);
    try {
      const response = await getAllMemberAuditLogs(nextLimit, nextOffset);
      const filteredLogs = (response.audit_logs || []).filter((log) =>
        AI_ACTION_TYPES.has(log.action_type)
      );
      setLogs(filteredLogs);
      setTotalCount(response.total_count || 0);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to fetch AI logs');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdminUser(adminInfo.admin)) return;
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminInfo.admin]);

  const maxOffset = Math.max(0, totalCount - Math.max(1, limit));
  const currentStart = totalCount === 0 ? 0 : offset + 1;
  const currentEnd = Math.min(offset + logs.length, totalCount);
  const canPrev = offset > 0;
  const canNext = offset + limit < totalCount;

  if (!isSuperAdminUser(adminInfo.admin)) return null;
  if (isLoading) return <Loader />;

  return (
    <div className='global-audit-logs-page page-container'>
      <div className='search-row-container sideBarPad'>
        <div className='search-and-filter-container maxW100'>
          <div className='count-container'>AI Logs ({totalCount})</div>
          <div className='filters-container'>
            <div className='pagination-meta'>
              {totalCount > 0 ? (
                <span>
                  Showing {currentStart}-{currentEnd} of {totalCount}
                </span>
              ) : (
                <span>Showing 0</span>
              )}
            </div>
            <select
              value={limit}
              onChange={(e) => {
                const nextLimit = Number(e.target.value);
                const safeLimit = Number.isFinite(nextLimit) && nextLimit > 0 ? nextLimit : 200;
                const nextOffset = 0;
                setLimit(safeLimit);
                setOffset(nextOffset);
                fetchLogs(safeLimit, nextOffset);
              }}
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
            <button
              className='btn secondary'
              disabled={!canPrev}
              onClick={() => {
                const nextOffset = Math.max(0, offset - limit);
                setOffset(nextOffset);
                fetchLogs(limit, nextOffset);
              }}
            >
              Prev
            </button>
            <button
              className='btn secondary'
              disabled={!canNext}
              onClick={() => {
                const nextOffset = Math.min(maxOffset, offset + limit);
                setOffset(nextOffset);
                fetchLogs(limit, nextOffset);
              }}
            >
              Next
            </button>
            <button className='btn secondary' onClick={() => fetchLogs()}>
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className='audit-logs-table'>
        <div className='table-header'>
          <div className='header-cell' style={{maxWidth: "200px"}}>Date</div>
          <div className='header-cell' style={{maxWidth: "200px"}}>Time</div>
          <div className='header-cell' style={{maxWidth: "300px"}}>Who did it</div>
          <div className='header-cell'>What did they do</div>
          <div className='header-cell'>Action</div>
        </div>

        <div className='table-body'>
          {logs.length === 0 ? (
            <div className='empty-state'>No AI logs found.</div>
          ) : (
            logs.map((log) => {
              const { date, time } = formatDateTime(log.performed_at);
              return (
                <div key={log.id} className='table-row'>
                  <div className='table-cell' style={{maxWidth: "200px"}}>{date}</div>
                  <div className='table-cell' style={{maxWidth: "200px"}}>{time}</div>
                  <div className='table-cell' style={{maxWidth: "300px"}}>
                    {log.performed_by_name}
                    <div className='meta role-badge'>{formatRoleLabel(log.performed_by_role)}</div>
                  </div>
                  <div className='table-cell'>{getActionPreview(log)}</div>
                  <div className='table-cell'>
                    <button className='btn small secondary' onClick={() => setSelectedLog(log)}>
                      View details
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {selectedLog && (
        <div className='details-backdrop' onClick={() => setSelectedLog(null)}>
          <div className='details-modal' onClick={(e) => e.stopPropagation()}>
            <div className='details-header'>
              <h3>Audit Log Details</h3>
              <button className='btn small secondary' onClick={() => setSelectedLog(null)}>
                Close
              </button>
            </div>
            <div className='details-meta'>
              <div><b>Member ID:</b> {selectedLog.member_id || '-'}</div>
              <div><b>Action Type:</b> {selectedLog.action_type}</div>
              <div><b>Performed By:</b> {selectedLog.performed_by_name}</div>
              <div><b>Role:</b> {formatRoleLabel(selectedLog.performed_by_role)}</div>
              <div><b>Performed At:</b> {selectedLog.performed_at}</div>
            </div>

            {selectedLog.action_type === MEMBER_PROFILES_MERGED_ACTION ? (
              (() => {
                const { profileRows, reassignedTables } = buildMergeProfileDetailRows(
                  selectedLog.action_details
                );
                return (
                  <>
                    <div className='details-section'>
                      <div className='details-section-title'>Profile merge details</div>
                      {profileRows.length > 0 ? (
                        <div className='merge-profile-detail-grid'>
                          {profileRows.map((row) => (
                            <div key={row.label} className='merge-profile-detail-row'>
                              <div className='merge-profile-detail-label'>{row.label}</div>
                              <div className='merge-profile-detail-value'>{row.value}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className='details-json'>No profile details recorded.</div>
                      )}
                    </div>
                    {reassignedTables && Object.keys(reassignedTables).length > 0 && (
                      <div className='details-section'>
                        <div className='details-section-title'>Reassigned tables</div>
                        <div className='details-json'>
                          {renderJsonValue(reassignedTables)}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()
            ) : getQuestionAnswerForLog(selectedLog.action_type, selectedLog.action_details) ? (
              <>
                <div className='details-section'>
                  <div className='details-section-title'>Questions &amp; Answers</div>
                  <div className='qa-grid'>
                    <div className='qa-item'>
                      <div className='qa-label'>Question</div>
                      <div className='qa-value'>
                        {getQuestionAnswerForLog(selectedLog.action_type, selectedLog.action_details)
                          ?.question || '—'}
                      </div>
                    </div>
                    <div className='qa-item'>
                      <div className='qa-label'>Answer</div>
                      <div className='qa-value'>
                        {getQuestionAnswerForLog(selectedLog.action_type, selectedLog.action_details)
                          ?.answer || '—'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className='details-section'>
                  <div className='details-section-title'>Other information</div>
                  <div className='details-json'>
                    {renderJsonValue(
                      buildOtherInfoDetails(selectedLog.action_details, [
                        ...(getQuestionAnswerForLog(selectedLog.action_type, selectedLog.action_details)
                          ?.omitKeys || []),
                      ])
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className='details-section'>
                <div className='details-section-title'>Other information</div>
                <div className='details-json'>{renderJsonValue(selectedLog.action_details)}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalAuditLogs;
